import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EnvironmentService } from '@evorait/pace-environment';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { ENDPOINTS } from '../../../enums/endpoints.enum';
import {
    setUserStoreValue,
    setUserStoreValueFailed,
    setUserStoreValueSuccess,
} from '../../user-store/actions/user-store.actions';
import { selectUserStoreSettled } from '../../user-store/selectors/user-store.selector';
import {
    clearLocationTrackerSaveMessage,
    locationTrackerDraftSeeded,
    openLocationTrackerPage,
    privacyPerimeterRequestFailed,
    privacyPerimeterResolved,
    requestPrivacyPerimeter,
    saveLocationTrackerUserConfig,
    saveLocationTrackerUserConfigFailed,
    saveLocationTrackerUserConfigSuccess,
} from '../actions/location-tracker.actions';
import { configStateToLocationSyncPayload, configStateToWireConfig } from '../location-tracker.converters';
import {
    LOCATION_TRACKER_STORE_KEY,
    LocationTrackerErrorKey,
    SAVE_MESSAGE_VISIBLE_MS,
} from '../location-tracker.state';
import {
    selectLocationTrackerUserConfig,
    selectStoredLocationTrackerConfig,
} from '../selectors/location-tracker.selector';

/**
 * Owns the location-tracker page's draft-seed flow and the geolocation lookup. The `LocationTracker`
 * key itself is no longer loaded, saved, or persisted here — the user-store slice is the single
 * source of truth for it (see `UserStoreEffects`), and this class only reads/writes through that
 * slice's actions.
 */
@Injectable()
export class LocationTrackerEffects {
    private readonly locationSyncUrl: string;

    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly http: HttpClient,
        private readonly environmentService: EnvironmentService,
        private readonly offlineService: OfflineService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        this.locationSyncUrl = this.environmentService.apiBase + ENDPOINTS.LocationSync;
    }

    /**
     * Seeds the draft from the user store once it has settled.
     *
     * Waits for `selectUserStoreSettled`, not merely "loaded": a cache rehydration also reports as
     * loaded, and seeding the form from the cache and then never refreshing it would show stale
     * settings for the rest of the session. Waiting for the backend to answer — a failure counts
     * as settled too, so an offline user still gets the cached mirror — preserves the guarantee
     * the old route resolver gave: the page never renders a value it is not prepared to keep.
     */
    public seedLocationTrackerDraft$ = createEffect(() =>
        this.actions$.pipe(
            ofType(openLocationTrackerPage),
            switchMap(() => this.store.select(selectUserStoreSettled).pipe(filter(Boolean), take(1))),
            concatLatestFrom(() => this.store.select(selectStoredLocationTrackerConfig)),
            map(([, config]) => locationTrackerDraftSeeded({ config })),
        ),
    );

    /**
     * Look up the device's current position. Owns the "geolocation missing" branch and the
     * `GeolocationPositionError.code` -> {@link LocationTrackerErrorKey} mapping, moved verbatim
     * from `LocationTrackerComponent.selectLocation`. The reducer truncates the raw coordinates
     * for privacy once they land in the draft.
     */
    public requestPrivacyPerimeter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(requestPrivacyPerimeter),
            switchMap(() => this.getCurrentPosition()),
        ),
    );

    /** Writes the current draft into the user store under `LOCATION_TRACKER_STORE_KEY`. */
    public saveLocationTrackerUserConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(saveLocationTrackerUserConfig),
            concatLatestFrom(() => this.store.select(selectLocationTrackerUserConfig)),
            map(([, config]) =>
                setUserStoreValue({ key: LOCATION_TRACKER_STORE_KEY, value: configStateToWireConfig(config) }),
            ),
        ),
    );

    /**
     * Tells the PACE FX function's `/api/v1/LocationSync` endpoint (which forwards it on to SENSE)
     * whether the user currently consents to being tracked, whenever the draft is saved.
     *
     * Fire-and-forget, like `LocationTrackerCompanionPushEffects.sendCompanionPush$`: this is a
     * side notification to a downstream system, not the save itself — `saveLocationTrackerUserConfig$`
     * above (via the user store) is the source of truth, and its success/failure banner must not
     * depend on this call. A failure here is only logged, never surfaced to the technician.
     *
     * Unlike the companion push, this one must survive being offline rather than just dropping: a
     * consent change is exactly the kind of thing that must not be silently lost because the
     * technician happened to save it in a dead zone. So this deliberately does NOT set
     * `ONLINE_HEADER` — `ENDPOINTS.LocationSync` is registered with `PaceOfflineModule` (see the
     * app's root module, mirroring `ENDPOINTS.AzUserStore`), so `OfflineInterceptorService` queues
     * the POST (even when online, in case the connection drops mid-send) and `RequestQueueEffects`
     * replays it once connectivity returns. `sendOfflineData` is fired the same way
     * `UserStoreEffects.putUserStoreValue$` fires it: on its own subscription, after the write has
     * already queued/succeeded, purely to flush immediately instead of waiting for some unrelated
     * sync — its failure (nothing to flush to while offline) must never turn this effect's own
     * success into an error.
     *
     * A stale consent value replayed after several offline saves is not a correctness problem: the
     * queue replays in order, so the last request applied is always the most recent value the user
     * actually saved, same as `AzUserStore`'s upsert queue.
     */
    public syncLocationTrackingPermission$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(saveLocationTrackerUserConfig),
                concatLatestFrom(() => this.store.select(selectLocationTrackerUserConfig)),
                concatMap(([, config]) =>
                    this.http.post<void>(this.locationSyncUrl, configStateToLocationSyncPayload(config)).pipe(
                        tap(() => {
                            this.offlineService.sendOfflineData().subscribe({
                                error: (error: unknown) =>
                                    this.log.error(
                                        `LocationTrackerEffects: syncLocationTrackingPermission$: sendOfflineData failed:`,
                                        error,
                                    ),
                            });
                        }),
                        catchError((error) => {
                            this.log.error(
                                `LocationTrackerEffects: syncLocationTrackingPermission$: sync failed: ${String(error)}`,
                            );
                            return EMPTY;
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Reports a save as succeeded once the user store confirms the `LocationTracker` write.
     *
     * `setUserStoreValueSuccess` means "accepted locally / queued for Azure" — the offline
     * interceptor answers immediately, before the real backend has necessarily seen the write (see
     * its doc comment in `user-store.actions.ts`) — NOT a backend acknowledgement, which is a
     * deliberate behavior change from the old direct-POST flow: the save banner now confirms the
     * write was accepted, and an offline save is queued instead of failing outright.
     */
    public reportLocationTrackerSaveSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setUserStoreValueSuccess),
            filter(({ key }) => key === LOCATION_TRACKER_STORE_KEY),
            map(() => saveLocationTrackerUserConfigSuccess()),
        ),
    );

    /** Reports a save as failed when the user store's `LocationTracker` write fails. */
    public reportLocationTrackerSaveFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setUserStoreValueFailed),
            filter(({ key }) => key === LOCATION_TRACKER_STORE_KEY),
            map(() => saveLocationTrackerUserConfigFailed()),
        ),
    );

    /** Hide the save confirmation/error once it has been shown long enough. */
    public clearSaveMessageAfterDelay$ = createEffect(() =>
        this.actions$.pipe(
            ofType(saveLocationTrackerUserConfigSuccess, saveLocationTrackerUserConfigFailed),
            switchMap(() => timer(SAVE_MESSAGE_VISIBLE_MS)),
            map(() => clearLocationTrackerSaveMessage()),
        ),
    );

    /** Wraps `navigator.geolocation.getCurrentPosition` — a callback API — in an observable of one action. */
    private getCurrentPosition(): Observable<Action> {
        if (!navigator.geolocation) {
            return of(privacyPerimeterRequestFailed({ errorKey: LocationTrackerErrorKey.NotSupported }));
        }

        return new Observable<Action>((subscriber) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    subscriber.next(
                        privacyPerimeterResolved({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        }),
                    );
                    subscriber.complete();
                },
                (error) => {
                    subscriber.next(privacyPerimeterRequestFailed({ errorKey: this.mapGeolocationError(error) }));
                    subscriber.complete();
                },
            );
        });
    }

    /** `GeolocationPositionError.code` -> translation key, moved verbatim from `LocationTrackerComponent.selectLocation`. */
    private mapGeolocationError(error: GeolocationPositionError): LocationTrackerErrorKey {
        switch (error.code) {
            case error.PERMISSION_DENIED:
                return LocationTrackerErrorKey.PermissionDenied;
            case error.POSITION_UNAVAILABLE:
                return LocationTrackerErrorKey.PositionUnavailable;
            case error.TIMEOUT:
                return LocationTrackerErrorKey.Timeout;
            default:
                return LocationTrackerErrorKey.Unknown;
        }
    }
}
